import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import ReactHtmlParser from 'react-html-parser'
import marked from 'marked'
import endPoints1Api from '../../services/end_points_1'
import { getReg } from '../../utils/fc'
import './atendimentoOikos.css'

const ERROS = {
  mensagem_invalida: 'A mensagem não pode ficar vazia.',
  session_id_invalido: 'A conversa não pôde continuar. Atualize a página e descreva a dor de novo.',
  erro_ao_criar_sessao_opencode: 'Não foi possível abrir a conversa com o atendimento.',
  erro_ao_obter_resposta_opencode: 'O atendimento não devolveu uma resposta.',
  caminho_nao_liberado_opencode: 'O atendimento tentou abrir um caminho que não está liberado e a conversa foi encerrada.',
  limite_modelo_opencode: 'O modelo gratuito atingiu o limite de uso do dia. Tente de novo mais tarde.',
  resposta_vazia_opencode: 'O atendimento devolveu uma resposta vazia.',
  erro_ao_consultar_opencode: 'Não foi possível consultar o atendimento.',
}

function mensagemErro(codigo) {
  const chave = String(codigo || '').trim()
  return ERROS[chave] || 'Não foi possível consultar o atendimento.'
}

function renderizarResposta(texto) {
  const seguro = String(texto || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return ReactHtmlParser(marked(seguro, { breaks: true, gfm: true }))
}

function ehVeredito(texto) {
  return /Classificação:\s*(Dúvida|Duvida|Bug|Melhoria)/i.test(String(texto || ''))
}

function formatarQuando(ms) {
  const valor = Number(ms)
  if (!valor) return ''
  return new Date(valor).toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const FERRAMENTAS_ATENDIMENTO = {
  read: true,
  grep: false,
  glob: false,
  list: false,
  bash: true,
  write: true,
  webfetch: false,
  skill: false,
  task: false,
  question: false,
  todowrite: false,
  websearch: false,
  apply_patch: false,
}

function montarPrimeiraMensagem(texto, nomeExibicao) {
  const nome = String(nomeExibicao || '').trim()
  const quem = nome
    ? `Quem foi atendido: ${nome}`
    : 'Quem foi atendido: não informado'
  return [
    'Siga a skill oikos-chat-atendimento.',
    'Leia este arquivo e obedeça a ele. Não copie as regras daqui: elas estão só nesse arquivo.',
    '/home/oikos/oikos-ops/skills/oikos-chat-atendimento/SKILL.md',
    'Se a skill apontar outra skill, leia /home/oikos/oikos-ops/skills/<nome>/SKILL.md.',
    'Grave arquivo só em /home/oikos/oikos-ops/reports/oikos-chat-atendimento/, e só quando a skill mandar.',
    'Se o resultado confirmado for Melhoria e a subclassificação for Aprimoramento de uma funcionalidade já existente, depois de gravar esse arquivo leia e obedeça /home/oikos/oikos-ops/skills/localizar-aprimoramento/SKILL.md.',
    'Nessa segunda skill, pode gravar em /home/oikos/oikos-ops/reports/localizar-aprimoramento/.',
    'Quando localizar-aprimoramento gravar o mapa, leia e obedeça /home/oikos/oikos-ops/skills/publicar-atendimento/SKILL.md.',
    'Se o resultado confirmado for Bug, depois de gravar esse arquivo leia e obedeça /home/oikos/oikos-ops/skills/localizar-bug/SKILL.md.',
    'Nessa skill, pode gravar em /home/oikos/oikos-ops/reports/localizar-bug/.',
    'Quando localizar-bug gravar a localização, leia e obedeça /home/oikos/oikos-ops/skills/publicar-atendimento/SKILL.md.',
    'Nessa terceira skill, grave em /home/oikos/oikos-ops/reports/publicar-atendimento/.',
    'A publicação é o comando publicar.py com --config. Não abra /home/oikos/oikos-ops/config.yaml nem o .env.',
    'Se o resultado confirmado for Melhoria e a subclassificação for Novo projeto de desenvolvimento, depois de gravar o arquivo do atendimento leia e obedeça /home/oikos/oikos-ops/skills/orquestrar-entrevista-usuario/SKILL.md, com esse markdown como contexto de partida.',
    'Nessa skill, pode gravar em /home/oikos/ecossis-oikos-web/docs/planejamento/usuarios/ e, se ela chegar na Fase C, em /home/oikos/oikos-ops/reports/cruzar-lacunas-requisito/.',
    'Não leia nem grave fora dos caminhos que a skill em execução indicar.',
    'A tela já cumprimentou o colaborador. Não cumprimente de novo.',
    quem,
    'Copie esse nome para Quem foi atendido no resultado. Não pergunte o nome e não invente outro.',
    'Na primeira mensagem, só entreviste. Não grave arquivo e não chame outra skill até o colaborador confirmar a classificação numa mensagem seguinte.',
    '',
    'Mensagem do colaborador:',
    texto,
  ].join('\n')
}

const AtendimentoOikos = () => {
  const { user } = useSelector(state => state.auth)
  const pfId = user?.pf_id
  const [nome, setNome] = useState(user?.pf_nomeExibicao || '')
  const [mensagens, setMensagens] = useState([])
  const [mensagemAtual, setMensagemAtual] = useState('')
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState('')
  const [sessionId, setSessionId] = useState('')
  const [podeSair, setPodeSair] = useState(false)
  const [sessoes, setSessoes] = useState([])
  const [modo, setModo] = useState('nova')
  const [sessaoLida, setSessaoLida] = useState('')
  const [carregandoLista, setCarregandoLista] = useState(false)
  const [carregandoSessao, setCarregandoSessao] = useState(false)
  const skillIniciada = useRef(false)
  const historicoRef = useRef(null)
  const rascunho = useRef({ mensagens: [], sessionId: '', podeSair: false, skillIniciada: false })

  useEffect(() => {
    let ativo = true
    if (!pfId) return undefined
    getReg('pf', pfId).then((registro) => {
      if (!ativo) return
      const exibicao = registro?.pf_nomeExibicao
      if (exibicao) setNome(exibicao)
    }).catch(() => {})
    return () => {
      ativo = false
    }
  }, [pfId])

  const carregarSessoes = useCallback(async () => {
    setCarregandoLista(true)
    try {
      const respostaHttp = await endPoints1Api.post('/', { acao: 'opencode_sessoes' })
      const dados = respostaHttp?.data || {}
      if (!dados.ok || !Array.isArray(dados.sessoes)) {
        throw new Error(dados.message || 'erro_ao_consultar_opencode')
      }
      setSessoes(dados.sessoes)
    } catch (err) {
      const codigo = err?.data?.message || err?.message
      setErro(mensagemErro(codigo))
    } finally {
      setCarregandoLista(false)
    }
  }, [])

  useEffect(() => {
    carregarSessoes()
  }, [carregarSessoes])

  useEffect(() => {
    const el = historicoRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [mensagens, carregando, carregandoSessao, erro])

  const abrirNova = () => {
    const guardado = rascunho.current
    setModo('nova')
    setSessaoLida('')
    setMensagens(guardado.mensagens)
    setSessionId(guardado.sessionId)
    setPodeSair(guardado.podeSair)
    skillIniciada.current = guardado.skillIniciada
    setErro('')
  }

  const verSessao = async (id) => {
    const idAtual = modo === 'nova' ? sessionId : rascunho.current.sessionId
    if (id && id === idAtual) {
      if (modo === 'leitura') abrirNova()
      return
    }
    if (modo === 'nova') {
      rascunho.current = {
        mensagens,
        sessionId,
        podeSair,
        skillIniciada: skillIniciada.current,
      }
    }
    setModo('leitura')
    setSessaoLida(id)
    setMensagens([])
    setErro('')
    setCarregandoSessao(true)
    try {
      const respostaHttp = await endPoints1Api.post('/', {
        acao: 'opencode_sessao',
        session_id: id,
      })
      const dados = respostaHttp?.data || {}
      if (!dados.ok || !Array.isArray(dados.mensagens)) {
        throw new Error(dados.message || 'erro_ao_consultar_opencode')
      }
      setMensagens(dados.mensagens.map((mensagem) => ({
        role: mensagem.role,
        content: mensagem.content,
        veredito: mensagem.role === 'assistant' && ehVeredito(mensagem.content),
      })))
    } catch (err) {
      const codigo = err?.data?.message || err?.message
      setErro(mensagemErro(codigo))
    } finally {
      setCarregandoSessao(false)
    }
  }

  const enviar = async () => {
    const texto = mensagemAtual.trim()
    if (!texto || carregando || podeSair || modo !== 'nova') return

    const primeira = !skillIniciada.current
    setMensagens(lista => [...lista, { role: 'user', content: texto }])
    setMensagemAtual('')
    setErro('')
    setCarregando(true)

    try {
      const corpo = {
        acao: 'opencode_teste',
        mensagem: primeira ? montarPrimeiraMensagem(texto, nome) : texto,
        tools: FERRAMENTAS_ATENDIMENTO,
      }
      if (sessionId) corpo.session_id = sessionId

      const respostaHttp = await endPoints1Api.post('/', corpo)
      const dados = respostaHttp?.data || {}
      if (!dados.ok || !dados.resposta) {
        throw new Error(dados.message || 'erro_ao_consultar_opencode')
      }
      if (dados.session_id) setSessionId(dados.session_id)
      skillIniciada.current = true
      if (dados.pode_sair) setPodeSair(true)
      setMensagens(lista => {
        const proxima = [...lista, {
          role: 'assistant',
          content: dados.resposta,
          veredito: ehVeredito(dados.resposta),
        }]
        rascunho.current = {
          mensagens: proxima,
          sessionId: dados.session_id || sessionId,
          podeSair: Boolean(dados.pode_sair),
          skillIniciada: true,
        }
        return proxima
      })
      carregarSessoes()
    } catch (err) {
      const codigo = err?.data?.message || err?.message
      setErro(mensagemErro(codigo))
    } finally {
      setCarregando(false)
    }
  }

  if (!pfId) {
    return (
      <div className="page-content">
        <div className="atend">
          <div className="atend-denied">Você não tem permissão para acessar essa tela</div>
        </div>
      </div>
    )
  }

  const cumprimento = nome
    ? `Olá ${nome}. Descreva sua dor ao utilizar o sistema do Oikos.`
    : 'Olá. Descreva sua dor ao utilizar o sistema do Oikos.'

  return (
    <div className="page-content">
      <div className="atend">
        <div className="atend-grid">
          <aside className="atend-side" aria-label="Conversas">
            <button
              type="button"
              className={['atend-nova', modo === 'nova' ? 'is-active' : ''].filter(Boolean).join(' ')}
              onClick={abrirNova}
            >
              Nova conversa
            </button>
            <div className="atend-side-list">
              {carregandoLista && sessoes.length === 0 && (
                <p className="atend-side-empty">Carregando conversas</p>
              )}
              {!carregandoLista && sessoes.length === 0 && (
                <p className="atend-side-empty">Nenhuma conversa ainda.</p>
              )}
              {sessoes.map((sessao) => {
                const ativa = modo === 'leitura'
                  ? sessao.id === sessaoLida
                  : sessao.id === sessionId
                return (
                  <button
                    key={sessao.id}
                    type="button"
                    className={['atend-sessao', ativa ? 'is-active' : ''].filter(Boolean).join(' ')}
                    onClick={() => verSessao(sessao.id)}
                  >
                    <span className="atend-sessao-titulo">{sessao.titulo}</span>
                    <span className="atend-sessao-quando">{formatarQuando(sessao.atualizado)}</span>
                  </button>
                )
              })}
            </div>
          </aside>
          <section className="atend-main" aria-label="Conversa">
            <p className="atend-lead">
              {modo === 'leitura'
                ? (sessoes.find((sessao) => sessao.id === sessaoLida)?.titulo || 'Conversa')
                : cumprimento}
            </p>
            <div className="atend-history" ref={historicoRef}>
              {mensagens.length === 0 && !carregando && !carregandoSessao && (
                <p className="atend-empty">
                  {modo === 'leitura'
                    ? 'Esta conversa não tem texto para mostrar.'
                    : 'Descreva a sua dor para iniciar a conversa.'}
                </p>
              )}
              {mensagens.map((mensagem, indice) => (
                <article
                  key={`${mensagem.role}-${indice}`}
                  className={[
                    'atend-card',
                    mensagem.role === 'user' ? 'atend-card--user' : 'atend-card--assistant',
                    mensagem.veredito ? 'atend-card--veredito' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <span className="atend-card-who">
                    {mensagem.role === 'user' ? (modo === 'leitura' ? 'Colaborador' : 'Você') : 'Atendimento'}
                  </span>
                  {mensagem.role === 'assistant' ? (
                    <div className="atend-md">{renderizarResposta(mensagem.content)}</div>
                  ) : (
                    mensagem.content
                  )}
                </article>
              ))}
              {(carregando || carregandoSessao) && (
                <div className="atend-typing" role="status" aria-live="polite">
                  {carregandoSessao ? 'Abrindo conversa' : 'Digitando'}
                  <span className="atend-dots" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </span>
                </div>
              )}
            </div>
            {erro && (
              <div className="atend-error" role="alert">{erro}</div>
            )}
            <form
              className="atend-composer"
              onSubmit={(evento) => {
                evento.preventDefault()
                enviar()
              }}
            >
              <label className="sr-only" htmlFor="atend-mensagem">Mensagem</label>
              <textarea
                id="atend-mensagem"
                rows={3}
                placeholder={modo === 'leitura' ? 'Somente leitura' : (podeSair ? 'Atendimento registrado' : 'Descreva sua dor')}
                value={mensagemAtual}
                disabled={carregando || podeSair || modo === 'leitura'}
                onChange={(evento) => setMensagemAtual(evento.target.value)}
                onKeyDown={(evento) => {
                  if (evento.key === 'Enter' && !evento.shiftKey) {
                    evento.preventDefault()
                    enviar()
                  }
                }}
              />
              <button className="atend-send" type="submit" disabled={carregando || podeSair || modo === 'leitura' || !mensagemAtual.trim()}>
                Enviar
              </button>
            </form>
          </section>
        </div>
      </div>
    </div>
  )
}

export default AtendimentoOikos
